import { useState } from "react";
import { useIntl } from "react-intl";
import {
  Globe,
  Trash2,
  ExternalLink,
  Eye,
  EditIcon,
  Loader,
} from "lucide-react";
import Message from "../../../../../i18n/Message";
import label from "../../../../../i18n/label";
import dayjs from "dayjs";
import WebpageContentDialog from "./WebpageContentDialog";
import RagStatusBadge from "../../shared/RagStatusBadge";

import "./WebpageAssetItem.scss";

const WebpageAssetItem = ({ asset, onDelete, onUpdate }) => {
  const intl = useIntl();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(asset.name);
  const [editedUrl, setEditedUrl] = useState(asset.url);

  const handleDeleteConfirmed = () => {
    onDelete(asset);
    setShowDeleteConfirm(false);
  };

  const handleSave = () => {
    if (!editedName.trim() || !editedUrl.trim()) {
      return;
    }
    onUpdate(asset, {
      name: editedName,
      url: editedUrl,
    });
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditedName(asset.name);
    setEditedUrl(asset.url);
    setIsEditing(false);
  };

  const handleOpenUrl = () => {
    window.open(asset.url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="webpage_asset_item card border-radius-lg">
      <div className="asset_content">
        <div className="asset_icon">
          <Globe size="24" color="#344767" />
        </div>
        <div className="asset_details">
          {isEditing ? (
            <>
              <input
                className="form-control form-control-sm"
                type="text"
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                placeholder={label(intl, "webpage_name")}
              />
              <input
                className="form-control form-control-sm mt-2"
                type="url"
                value={editedUrl}
                onChange={(e) => setEditedUrl(e.target.value)}
                placeholder={label(intl, "webpage_url")}
              />

              <div className="asset_actions">
                <button
                  className="btn btn-sm bg-gradient-primary"
                  onClick={handleSave}
                >
                  <Message id="save" />
                </button>
                <button className="btn btn-sm no-btn" onClick={handleCancel}>
                  <Message id="cancel" />
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="asset_name">{asset.name}</div>
              <div className="asset_url">
                <a
                  href={asset.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  {asset.url}
                </a>
              </div>

              {/* Crawling Status */}
              {asset.includeSubPages && asset.crawlingStatus && (
                <div className="crawling_status">
                  {asset.crawlingStatus === "IN_PROGRESS" && (
                    <div className="status_badge status_in_progress">
                      <Loader size="14" className="spinner" />
                      <Message id="crawling_in_progress" />
                      {asset.crawlingPagesFound > 0 && (
                        <span className="pages_count">
                          {" "}
                          ({asset.crawlingPagesFound}{" "}
                          <Message id="pages_found" />)
                        </span>
                      )}
                    </div>
                  )}
                  {asset.crawlingStatus === "COMPLETED" && (
                    <div className="status_badge status_completed">
                      <Message id="crawling_completed" />
                      {asset.crawlingPagesFound > 0 && (
                        <span className="pages_count">
                          {" "}
                          ({asset.crawlingPagesFound} <Message id="pages" />)
                        </span>
                      )}
                    </div>
                  )}
                  {asset.crawlingStatus === "FAILED" && (
                    <div className="status_badge status_failed">
                      <Message id="crawling_failed" />
                    </div>
                  )}
                </div>
              )}

              <div className="asset_metadata">
                <span className="metadata_item">
                  <Message id="created_on" />:{" "}
                  {dayjs(asset.dateCreated).format("ll")}
                </span>
                {asset.contentSize > 0 && (
                  <span className="metadata_item">
                    {(asset.contentSize / 1024).toFixed(1)} KB
                  </span>
                )}
                {asset.contentTokenCount > 0 && (
                  <span className="metadata_item">
                    {asset.contentTokenCount} tokens
                  </span>
                )}
                {asset.ragStatus && (
                  <span className="metadata_item">
                    <RagStatusBadge status={asset.ragStatus} />
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="asset_actions">
        {!isEditing && (
          <>
            <button
              className="no-btn action-btn"
              onClick={() => setShowPreview(true)}
              title={label(intl, "preview_content")}
            >
              <Eye size="16" color="#344767" />
            </button>
            <button
              className="no-btn action-btn"
              onClick={handleOpenUrl}
              title={label(intl, "open_url")}
            >
              <ExternalLink size="16" color="#344767" />
            </button>
            <button
              className="no-btn action-btn"
              onClick={() => setIsEditing(true)}
              title={label(intl, "edit")}
            >
              <EditIcon size="16" color="#344767" />
            </button>

            <button
              className="no-btn action-btn"
              onClick={() => setShowDeleteConfirm(true)}
              title={label(intl, "delete")}
            >
              <Trash2 size="16" color="#344767" />
            </button>
            <div className="confirm_container">
              {showDeleteConfirm && (
                <>
                  <div
                    className="cover"
                    onClick={() => setShowDeleteConfirm(false)}
                  ></div>
                  <div className="confirm_panel">
                    <button
                      className="btn bg-gradient-primary btn-sm"
                      onClick={handleDeleteConfirmed}
                    >
                      <Message id="confirm_delete" />
                    </button>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>

      {showPreview && (
        <WebpageContentDialog
          asset={asset}
          onClose={() => setShowPreview(false)}
        />
      )}
    </div>
  );
};

export default WebpageAssetItem;
